import React, { useCallback, useContext, useEffect } from "react";
import { t, Trans } from "@lingui/macro";
import { PayPalScriptProvider, FUNDING, PayPalButtons } from "@paypal/react-paypal-js";
import { SubscribeContinueBlackButton } from "./SubscribeContinueBlackButton";
import {
  MembershipSubscriptionProvider,
  MembershipSubscriptionStatus,
} from "../../../types/graphql-global-types";
import CreatePayPalSubscription from "../../../graphql/CreatePayPalSubscription.graphql";
import { ApolloQueryResult, useMutation } from "@apollo/client";
import { logError } from "../../../utils/logging";
import { sendEvent } from "../../../utils/gtm";
import { getLocale } from "../../../utils/locale";
import { getWelcomeURL } from "../utils/getWelcomeURL";
import { getContentOrigin } from "../utils/getContentOrigin";
import { getPlanOrigin } from "../utils/getPlanOrigin";
import { GetSubscriptionInfo } from "../../../types/GetSubscriptionInfo";
import { LoginContext } from "../../../components/LoginProvider/LoginProvider";
import { PaymentContext } from "../contexts/PaymentProvider";
import { usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Locale } from "../../../types/Locale";
import useNameInputsWithUpdate from "../hooks/useNameInputsWithUpdate";
import useGeoCountryCode from "../../../graphql/useGeoCountryCode/useGeoCountryCode";
import { isUttLoaded, sendConversionToImpact } from "../utils/impact";
import { ImpactPayLoad } from "../../../types/ImpactTracking";

const PayPalSubscribeSection = ({
  updateName,
  postChildren,
  disclaimer,
  discount,
}: {
  updateName?: boolean;
  postChildren?: React.ReactNode;
  disclaimer?: string;
  discount?: boolean;
}) => {
  const { user } = useContext(LoginContext);

  const {
    selectedPlan,
    selectedMethod,
    isNew,
    isReady,
    setPaymentError,
    setPaymentLoading,
    subscription,
    refetchSubscription,
    pollMembershipStatus,
  } = useContext(PaymentContext);

  const visible =
    (selectedMethod === "PAYPAL" || subscription?.provider === MembershipSubscriptionProvider.PAYPAL) &&
    subscription?.provider !== MembershipSubscriptionProvider.STRIPE;

  const tt = useTranslations();
  const locale = getLocale();

  const contentOrigin = getContentOrigin();
  const planOrigin = getPlanOrigin();

  const { isNameInputValid, handleUpdateName, render } = useNameInputsWithUpdate({
    isDisabled: !isReady,
  });

  const { data: geoCountryCode } = useGeoCountryCode();

  const [createPayPalSubscription] = useMutation(CreatePayPalSubscription);

  // PayPal start checkout
  const paypalCreateSubscription = useCallback(
    async (data: any, actions: any, plan_id: string) => {
      setPaymentError(false);

      setPaymentLoading(`${new Date().getTime() / 1000}`);

      let firstName = user?.firstName;
      let lastName = user?.lastName;

      if (updateName) {
        try {
          const { firstName: newFirstName, lastName: newLastName } = await handleUpdateName();
          firstName = newFirstName;
          lastName = newLastName;
        } catch (error) {
          setPaymentLoading("false");
          return console.log("handleUpdateName error", error);
        }
      }

      const { data: subscriptionData } = await (refetchSubscription() as Promise<
        ApolloQueryResult<GetSubscriptionInfo>
      >);

      if (
        subscription?.provider !== MembershipSubscriptionProvider.PAYPAL &&
        typeof subscription?.provider !== "undefined"
      ) {
        return null;
      }

      if (
        subscriptionData?.membershipSubscription?.status === MembershipSubscriptionStatus.ACTIVE ||
        subscriptionData?.membershipSubscription?.status === MembershipSubscriptionStatus.NEW
      ) {
        return null;
      }

      return actions.subscription.create({
        plan_id,
        subscriber: {
          email_address: user?.email,
          name: {
            given_name: firstName,
            surname: lastName,
          },
        },
        application_context: {
          user_action: "CONTINUE", // By default it's SUBSCRIBE_NOW
        },
        custom_id: user?.userId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refetchSubscription, user?.email, user?.firstName, user?.lastName, user?.userId]
  );

  // PayPal approved checkout
  const paypalOnApprove = useCallback(
    async (data: any, actions: any) => {
      const { data: subscriptionData } = await (refetchSubscription() as Promise<
        ApolloQueryResult<GetSubscriptionInfo>
      >);

      if (
        subscriptionData?.membershipSubscription?.status === MembershipSubscriptionStatus.ACTIVE ||
        subscriptionData?.membershipSubscription?.status === MembershipSubscriptionStatus.NEW
      ) {
        return null;
      }

      return actions.subscription.activate().then(async () => {
        const purchaseMembershipEvent = {
          eventName: "signup",
          action: "purchase_membership",
          payload: {
            success: true,
            locale: locale!,
            payment_method: "paypal",
            plan: selectedPlan,
            trial_period_days: Number(process.env.STRIPE_TRIAL_PERIOD_DAYS) || 7,
          },
        } as const;

        try {
          try {
            await createPayPalSubscription({
              variables: {
                input: {
                  subscriptionId: data.subscriptionID,
                },
              },
            });
            setPaymentLoading(`${new Date().getTime() / 1000}`);
          } catch (error) {
            logError("createPayPalSubscription mutation failed");
            throw error;
          }

          try {
            const result = await pollMembershipStatus();
            if (result === true) {
              sendEvent(purchaseMembershipEvent);
              if (isUttLoaded() && user?.userId && user?.email) {
                const impactPayload: ImpactPayLoad = {
                  id: data.subscriptionID,
                  userId: user?.userId,
                  email: user?.email,
                  customerStatus: "SUCCESS",
                  plan: selectedPlan,
                };

                sendConversionToImpact(impactPayload);
              }

              return (window.location.href = getWelcomeURL(planOrigin || selectedPlan)!);
            }
          } catch (error) {
            throw Error("Couldn't find active PayPal subscription");
          }
        } catch (error) {
          setPaymentError(true);
          sendEvent({
            ...purchaseMembershipEvent,
            payload: {
              ...purchaseMembershipEvent.payload,
              success: false,
            },
          });
          console.error("Create PayPal subscription error: ", error);
        } finally {
          setPaymentLoading("false");
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createPayPalSubscription, locale, selectedPlan, contentOrigin, pollMembershipStatus]
  );

  // PayPal error checkout
  const paypalOnError = useCallback(async () => {
    setPaymentError(true);
    sendEvent({
      eventName: "signup",
      action: "purchase_membership",
      payload: {
        success: false,
        locale: locale!,
        payment_method: "paypal",
        plan: selectedPlan,
        trial_period_days: Number(process.env.STRIPE_TRIAL_PERIOD_DAYS) || 7,
      },
    });
    setPaymentLoading("false");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, selectedPlan]);

  // PayPal cancel checkout
  const paypalOnCancel = useCallback(async () => {
    setPaymentLoading("false");
  }, [setPaymentLoading]);

  if (user?.userId) {
    return (
      <div className={`${visible ? "" : "hidden"}`}>
        <PayPalScriptProvider
          options={{
            "client-id": process.env.GATSBY_PAYPAL_CLIENT_KEY as string,
            "data-user-id-token": user.userId as string,
            intent: "subscription",
            vault: true,
            components: "buttons",
            locale: locale === Locale.SV ? "sv_SE" : locale === Locale.ES ? "es_ES" : "en_US",
          }}
        >
          {postChildren}

          <p className="text-base m-0 mt-4">{tt.toContinue}</p>

          <p className={`text-sm opacity-75 m-0 mt-3 mb-3 ${disclaimer ? "text-left" : ""}`}>
            {disclaimer ? disclaimer : selectedPlan === "ANNUAL" ? tt.canCancelAnnual : tt.canCancelMonthly}
          </p>
          {!isNew && (
            <>
              {updateName && render}

              <div className="mt-6">
                {(!isReady || (updateName && !isNameInputValid)) && (
                  <SubscribeContinueBlackButton
                    hexColor="#212323"
                    loading={!isReady}
                    disabled={updateName && !isNameInputValid}
                    style={{ margin: "0 auto", borderRadius: "4px" }}
                  >
                    {updateName && !isNameInputValid && (
                      <>
                        <img
                          className="h-6"
                          src="data:image/svg+xml;base64,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"
                          alt="paypal logo"
                        />
                        <span className="ml-2 font-bold">Subscribe</span>
                      </>
                    )}
                  </SubscribeContinueBlackButton>
                )}

                <div
                  style={{
                    overflow: "hidden",
                    height: !isReady || (updateName && !isNameInputValid) ? "0" : "50px",
                    visibility: !isReady || (updateName && !isNameInputValid) ? "hidden" : "visible",
                    opacity: !isReady || (updateName && !isNameInputValid) ? "0" : "1",
                    pointerEvents: !isReady || (updateName && !isNameInputValid) ? "none" : "auto",
                  }}
                >
                  <PayPalButtonMemoized
                    locale={locale}
                    selectedPlan={selectedPlan}
                    paypalCreateSubscription={paypalCreateSubscription}
                    paypalOnApprove={paypalOnApprove}
                    paypalOnError={paypalOnError}
                    paypalOnCancel={paypalOnCancel}
                    discount={discount}
                  />
                </div>
              </div>
            </>
          )}
        </PayPalScriptProvider>
      </div>
    );
  } else {
    return null;
  }
};
export default React.memo(PayPalSubscribeSection);

const PayPalButton = ({
  locale,
  selectedPlan,
  paypalCreateSubscription,
  paypalOnApprove,
  paypalOnError,
  paypalOnCancel,
  discount,
}: {
  locale: Locale;
  selectedPlan: "ANNUAL" | "MONTHLY";
  paypalCreateSubscription: (data: any, actions: any, plan_id: string) => Promise<any>;
  paypalOnApprove: (data: any, actions: any) => Promise<any>;
  paypalOnError: () => Promise<void>;
  paypalOnCancel: () => Promise<void>;
  discount?: boolean;
}) => {
  const [{ options }, dispatch] = usePayPalScriptReducer();

  // When changing plan
  useEffect(() => {
    dispatch({
      type: "resetOptions",
      value: {
        ...options,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlan]);

  return (
    <PayPalButtons
      fundingSource={FUNDING.PAYPAL}
      createSubscription={(data: any, actions: any) => {
        if (discount) {
          return paypalCreateSubscription(data, actions, process.env.GATSBY_PAYPAL_YEARLY_DISCOUNTED_PLAN_ID!);
        }

        if (selectedPlan === "ANNUAL") {
          return paypalCreateSubscription(
            data,
            actions,
            locale === Locale.SV
              ? process.env.GATSBY_PAYPAL_SE_YEARLY_PLAN_ID!
              : locale === Locale.ES
              ? process.env.GATSBY_PAYPAL_ES_YEARLY_PLAN_ID!
              : process.env.GATSBY_PAYPAL_US_YEARLY_PLAN_ID!
          );
        }

        if (selectedPlan === "MONTHLY") {
          return paypalCreateSubscription(
            data,
            actions,
            locale === Locale.SV
              ? process.env.GATSBY_PAYPAL_SE_MONTHLY_PLAN_ID!
              : locale === Locale.ES
              ? process.env.GATSBY_PAYPAL_ES_MONTHLY_PLAN_ID!
              : process.env.GATSBY_PAYPAL_US_MONTHLY_PLAN_ID!
          );
        }
      }}
      onApprove={paypalOnApprove}
      onError={paypalOnError}
      onCancel={paypalOnCancel}
      style={{
        label: "subscribe",
        layout: "vertical",
        color: "black",
        shape: "rect",
        height: 50,
      }}
    />
  );
};

const PayPalButtonMemoized = React.memo(PayPalButton);

function useTranslations() {
  return {
    toContinue: (
      <Trans id="PayPal.toContinue">
        To continue, click <b>PayPal Subscribe</b> and log into PayPal using your email and password.
      </Trans>
    ),
    canCancelAnnual: t({
      id: "PayPal.canCancelAnnual",
      message:
        "You can cancel at anytime. You will not be charged until your free trial ends. If you don't cancel, you will be charged US$119 per year. You can cancel anytime and pay nothing.",
    }),
    canCancelMonthly: t({
      id: "PayPal.canCancelMonthly",
      message:
        "You can cancel at anytime. You will not be charged until your free trial ends. If you don't cancel, you will be charged US$14.99 per month. You can cancel anytime and pay nothing.",
    }),
  };
}
